import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { cn } from "@/lib/cn";

interface ConfirmOptions {
  title: string;
  description?: ReactNode;
  confirmLabel?: string;
  danger?: boolean;
}

interface PromptOptions {
  title: string;
  description?: ReactNode;
  label?: string;
  placeholder?: string;
  defaultValue?: string;
  confirmLabel?: string;
  required?: boolean;
  multiline?: boolean;
}

interface DialogsContextValue {
  confirm: (opts: ConfirmOptions) => Promise<boolean>;
  prompt: (opts: PromptOptions) => Promise<string | null>;
}

const DialogsContext = createContext<DialogsContextValue | null>(null);

// Imperative API, mirroring sonner's `toast()`: a module-level function
// backed by the single mounted <DialogsProvider>. Lets any handler call
// `await confirmDialog(...)` / `await promptDialog(...)` without wiring a
// hook through every component. No-ops safely if no provider is mounted.
let imperativeConfirm: DialogsContextValue["confirm"] | null = null;
let imperativePrompt: DialogsContextValue["prompt"] | null = null;

export function confirmDialog(opts: ConfirmOptions): Promise<boolean> {
  return imperativeConfirm ? imperativeConfirm(opts) : Promise.resolve(false);
}

export function promptDialog(opts: PromptOptions): Promise<string | null> {
  return imperativePrompt ? imperativePrompt(opts) : Promise.resolve(null);
}

type Active =
  | { kind: "confirm"; opts: ConfirmOptions }
  | { kind: "prompt"; opts: PromptOptions };

/**
 * App-wide replacement for window.confirm / window.prompt. Promise-based
 * so call sites read almost the same as before:
 *
 *   if (!(await confirm({ title: "Delete?" }))) return;
 *   const name = await prompt({ title: "Add phase", label: "Name", required: true });
 *
 * confirm resolves true/false; prompt resolves the entered string, or
 * null when cancelled/dismissed (mirroring window.prompt). One shared
 * dialog instance, mounted once via <DialogsProvider>.
 */
export function DialogsProvider({ children }: { children: ReactNode }) {
  const [active, setActive] = useState<Active | null>(null);
  const [value, setValue] = useState("");
  const resolver = useRef<((v: boolean | string | null) => void) | null>(null);

  const settle = useCallback((result: boolean | string | null) => {
    const r = resolver.current;
    resolver.current = null;
    setActive(null);
    r?.(result);
  }, []);

  const confirm = useCallback(
    (opts: ConfirmOptions) =>
      new Promise<boolean>((resolve) => {
        resolver.current = resolve as (v: boolean | string | null) => void;
        setActive({ kind: "confirm", opts });
      }),
    [],
  );

  const prompt = useCallback(
    (opts: PromptOptions) =>
      new Promise<string | null>((resolve) => {
        resolver.current = resolve as (v: boolean | string | null) => void;
        setValue(opts.defaultValue ?? "");
        setActive({ kind: "prompt", opts });
      }),
    [],
  );

  const isPrompt = active?.kind === "prompt";
  const trimmed = value.trim();
  const canConfirm = !isPrompt || !active.opts.required || trimmed.length > 0;

  const onConfirm = () => {
    if (!active) return;
    if (active.kind === "prompt") {
      if (active.opts.required && !trimmed) return;
      settle(trimmed);
    } else {
      settle(true);
    }
  };

  const onCancel = () => settle(isPrompt ? null : false);

  // Register the imperative API against this provider instance.
  useEffect(() => {
    imperativeConfirm = confirm;
    imperativePrompt = prompt;
    return () => {
      imperativeConfirm = null;
      imperativePrompt = null;
    };
  }, [confirm, prompt]);

  return (
    <DialogsContext.Provider value={{ confirm, prompt }}>
      {children}
      <Dialog open={active !== null} onOpenChange={(open) => !open && onCancel()}>
        {active && (
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{active.opts.title}</DialogTitle>
              {active.opts.description && (
                <DialogDescription>{active.opts.description}</DialogDescription>
              )}
            </DialogHeader>

            {active.kind === "prompt" && (
              <div className="py-1">
                {active.opts.label && (
                  <label className="mb-1.5 block text-sm font-medium text-ink-2">
                    {active.opts.label}
                  </label>
                )}
                {active.opts.multiline ? (
                  <textarea
                    autoFocus
                    rows={3}
                    value={value}
                    placeholder={active.opts.placeholder}
                    onChange={(e) => setValue(e.target.value)}
                    className="w-full rounded-lg border border-line-strong px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                ) : (
                  <input
                    autoFocus
                    type="text"
                    value={value}
                    placeholder={active.opts.placeholder}
                    onChange={(e) => setValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        onConfirm();
                      }
                    }}
                    className="w-full rounded-lg border border-line-strong px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                )}
              </div>
            )}

            <DialogFooter>
              <button
                onClick={onCancel}
                className="rounded-lg border border-line-strong px-4 py-2 text-sm font-medium text-ink-3 hover:bg-raised"
              >
                Cancel
              </button>
              <button
                onClick={onConfirm}
                disabled={!canConfirm}
                className={cn(
                  "rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-50",
                  active.kind === "confirm" && active.opts.danger
                    ? "bg-red-600 hover:bg-red-700"
                    : "bg-indigo-600 hover:bg-indigo-700",
                )}
              >
                {active.opts.confirmLabel ??
                  (active.kind === "confirm" ? "Confirm" : "OK")}
              </button>
            </DialogFooter>
          </DialogContent>
        )}
      </Dialog>
    </DialogsContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(DialogsContext);
  if (!ctx) throw new Error("useConfirm must be used within a DialogsProvider");
  return ctx.confirm;
}

export function usePrompt() {
  const ctx = useContext(DialogsContext);
  if (!ctx) throw new Error("usePrompt must be used within a DialogsProvider");
  return ctx.prompt;
}
